import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { Formik } from 'formik';
import { ThemeContext } from 'styled-components';
import colorToolsUtils from '../../utils/colorToolsUtils';
import AddColorDefaultModal from './AddColorDefaultModal';
import useAllColorUserCache from '../../services/useAllColorUserCache';
import feedbackOnStageUtils from '../../utils/feedbackOnStageUtils';
import FeedbackContext from '../feedbackContext/FeedbackContext';
import handleOnColorClick from '../../utils/handleOnColorClick';
import ColorNameLicense from '../ColorNameLicense';

const AddColorModal = ({ colorValue, onRequestClose, ...props }) => {
    const intl = useIntl();
    const [saving, setSaving] = useState(false);
    const [failed, setFailed] = useState(false);
    const theme = useContext(ThemeContext);
    const { setFeedbackOnStage } = useContext(FeedbackContext);

    return (
        <Formik initialValues={{ addColor: colorValue.slice(1) }}>
            {({ values }) => {
                const { addColor } = values;

                const addColorToHex = `#${addColor}`;

                const {
                    allColorsUserFromClient,
                    setAllColorsUserFromClient,
                    createColor,
                    user,
                    pathname,
                    client,
                } = useAllColorUserCache(() =>
                    setFeedbackOnStage(
                        feedbackOnStageUtils.colorAddedToLibrary(addColorToHex, theme, intl),
                    ),
                );

                const isFavorite = allColorsUserFromClient?.allColors?.data?.find(
                    (item) => item.color === addColorToHex,
                );

                const footerActions = [
                    {
                        label: intl.formatMessage({
                            id: 'addColor.modal.footer.addColor.label',
                        }),
                        icon: theme.site.feedback.icon.add,
                        btnType: 'link',
                        skin: 'muted',
                        size: 'md',
                        disabled: saving || !colorToolsUtils.isValidColor(addColor) || !!isFavorite,
                        onClick: async () => {
                            if (saving) return;
                            setSaving(true);
                            setFailed(false);
                            try {
                                await handleOnColorClick({
                                    currentColor: addColorToHex,
                                    currentId: isFavorite?.id,
                                    setAllColorsUserFromClient,
                                    allColorsUserFromClient,
                                    client,
                                    createColor,
                                    deleteColor: null,
                                    user,
                                    pathname,
                                });
                                onRequestClose();
                            } catch {
                                setFailed(true);
                            } finally {
                                setSaving(false);
                            }
                        },
                        form: 'addColorForm',
                        type: 'button',
                    },
                ];

                return (
                    <AddColorDefaultModal
                        addColor={addColor}
                        colorValue={colorValue}
                        onRequestClose={onRequestClose}
                        subtitle={
                            failed
                                ? intl.formatMessage({ id: 'library.save.error' })
                                : intl.formatMessage(
                                      { id: 'addColor.modal.subtitle' },
                                      {
                                          colorName: (
                                              <ColorNameLicense>
                                                  {colorToolsUtils.colorName(colorValue.slice(1))}
                                              </ColorNameLicense>
                                          ),
                                      },
                                  )
                        }
                        footerActions={footerActions}
                        {...props}
                    />
                );
            }}
        </Formik>
    );
};

AddColorModal.propTypes = {
    colorValue: PropTypes.string.isRequired,
    onRequestClose: PropTypes.func.isRequired,
};

export default AddColorModal;
